import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlaidLinkOnSuccess, PlaidLinkOnExit } from 'react-plaid-link';
import { usePlaidLinkWithLogging } from '../hooks/usePlaidLinkWithLogging';
import { Center, Spinner, Text, VStack, useToast } from '@chakra-ui/react';
import { apiFetch } from '../lib/api';
import { supabase } from '../lib/supabase';
import { useAuthContext } from '../contexts/AuthContext';

export const OAUTH_PENDING_KEY = 'xacto_plaid_oauth_pending';

export interface PlaidOAuthPending {
  linkToken: string;
  mode: 'add_bank' | 'reconnect' | 'signup';
  tokenId?: string;
  tenantId: string;
}

/**
 * Dedicated OAuth return handler for Plaid.
 *
 * PLAID_REDIRECT_URI must point to this route.
 * On OAuth return the bank appends ?oauth_state_id=... to the URL.
 * This component reads the pending link state from localStorage, re-initialises
 * Plaid Link with receivedRedirectUri, and completes the connection.
 *
 * Using localStorage (not sessionStorage) so App2App mobile flows and fresh
 * browser contexts that Chase/WF/BofA may open still find the token.
 */
export const PlaidOAuthReturn: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { session, loading: authLoading } = useAuthContext();
  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; });
  const isExchangingRef = useRef(false);

  // On OAuth return from bank, session may be lost due to SameSite cookie restrictions.
  // Store session in ref to handle exchange even if auth context updates.
  // If session is completely missing and we need it, redirect to login with return URL.
  
  const pending = useMemo<PlaidOAuthPending | null>(() => {
    const url = window.location.href;
    const hasOAuthState = url.includes('?oauth_state_id=');
    const raw = localStorage.getItem(OAUTH_PENDING_KEY);
    let parsed: PlaidOAuthPending | null = null;
    try {
      parsed = raw ? (JSON.parse(raw) as PlaidOAuthPending) : null;
    } catch (e) {
      console.error('[PlaidOAuthReturn] MOUNT — failed to parse pending state', { raw, error: e });
    }
    console.log('[PlaidOAuthReturn] MOUNT', {
      url,
      hasOAuthStateInUrl: hasOAuthState,
      oauthStateId: new URLSearchParams(url.split('?')[1] ?? '').get('oauth_state_id'),
      hasPendingInStorage: !!raw,
      pending: parsed
        ? { mode: parsed.mode, tokenId: parsed.tokenId, tenantId: parsed.tenantId, linkTokenPrefix: parsed.linkToken?.slice(0, 30) }
        : null,
      allPlaidKeys: Object.keys(localStorage).filter(k => k.startsWith('xacto_plaid')),
      hasSession: !!sessionRef.current,
      authLoading,
    });
    return parsed;
  }, []);

  const onSuccess: PlaidLinkOnSuccess = useCallback(
    async (publicToken, metadata) => {
      if (isExchangingRef.current) return;
      isExchangingRef.current = true;

      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) throw new Error('Session expired after bank redirect. Please log in and try again.');
      const tenantId = pending?.tenantId ?? '';

      console.log('[PlaidOAuthReturn] onSuccess RECEIVED', {
        mode: pending?.mode,
        institution: metadata.institution?.name,
        institutionId: metadata.institution?.institution_id,
        publicTokenPrefix: publicToken?.slice(0, 20),
        accountCount: metadata.accounts?.length,
        tenantId,
        tokenId: pending?.tokenId,
        hasAccessToken: !!accessToken,
        linkSessionId: metadata.link_session_id,
      });

      try {
        if (pending?.mode === 'reconnect') {
          console.log('[PlaidOAuthReturn] calling exchange-token for reconnect', { tenantId, tokenId: pending.tokenId });
          const response = await apiFetch(
            '/api/signup/exchange-token',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                publicToken,
                tenantId,
                institutionId: metadata.institution?.institution_id,
                institutionName: metadata.institution?.name,
                linkSessionId: metadata.link_session_id,
              }),
            },
            accessToken!
          );
          console.log('[PlaidOAuthReturn] reconnect exchange-token response', { status: response.status, ok: response.ok });
          if (!response.ok) throw new Error('Failed to exchange token on reconnect');
        } else {
          console.log('[PlaidOAuthReturn] calling exchange-token endpoint', { tenantId, institution: metadata.institution?.name });
          const response = await apiFetch(
            '/api/signup/exchange-token',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                publicToken,
                tenantId,
                institutionId: metadata.institution?.institution_id,
                institutionName: metadata.institution?.name,
                linkSessionId: metadata.link_session_id,
              }),
            },
            accessToken!
          );
          console.log('[PlaidOAuthReturn] exchange-token API response', { status: response.status, ok: response.ok });
          if (!response.ok) throw new Error('Failed to exchange token');
        }

        console.log('[PlaidOAuthReturn] onSuccess COMPLETE — clearing storage and navigating');
        localStorage.removeItem(OAUTH_PENDING_KEY);
        toast({
          title: pending?.mode === 'reconnect' ? 'Bank Reconnected' : 'Bank Connected',
          description: `${metadata.institution?.name || 'Your bank'} was connected successfully.`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      } catch (err) {
        console.error('[PlaidOAuthReturn] onSuccess error', {
          mode: pending?.mode,
          error: err instanceof Error ? err.message : String(err),
        });
        toast({
          title: 'Connection issue',
          description:
            'Bank authorization completed but we had trouble saving the connection. Please check your banks page.',
          status: 'warning',
          duration: 6000,
          isClosable: true,
        });
      } finally {
        navigate('/status', { replace: true });
      }
    },
    [pending, toast, navigate]
  );

  const onExit: PlaidLinkOnExit = useCallback(
    (err, metadata) => {
      if (err) {
        console.error('[PlaidOAuthReturn] onExit', {
          mode: pending?.mode,
          tenantId: pending?.tenantId ?? null,
          error_type: err.error_type,
          error_code: err.error_code,
          error_message: err.error_message,
          display_message: err.display_message,
          request_id: metadata?.request_id,
          link_session_id: metadata?.link_session_id,
          receivedRedirectUri: window.location.href,
        });
        if (err.error_code === 'INVALID_LINK_TOKEN') {
          localStorage.removeItem(OAUTH_PENDING_KEY);
          toast({
            title: 'Session expired',
            description:
              pending?.mode === 'reconnect'
                ? 'The reconnection session expired. Please try reconnecting from your banks page.'
                : 'The bank connection session expired. Please try again.',
            status: 'warning',
            duration: 6000,
            isClosable: true,
          });
          navigate('/status', { replace: true });
          return;
        }
      }
      localStorage.removeItem(OAUTH_PENDING_KEY);
      navigate('/status', { replace: true });
    },
    [navigate, toast, pending?.mode]
  );

  const { open, ready } = usePlaidLinkWithLogging({
    mode: pending?.mode === 'reconnect' ? 'reconnect' : pending?.mode === 'signup' ? 'signup' : 'add_bank',
    tenantId: pending?.tenantId ?? '',
    token: pending?.linkToken ?? '',
    onSuccess,
    onExit,
    receivedRedirectUri: window.location.href,
  });

  useEffect(() => {
    console.log('[PlaidOAuthReturn] usePlaidLink state', {
      ready,
      hasLinkToken: !!pending?.linkToken,
      linkTokenPrefix: pending?.linkToken?.slice(0, 30),
      receivedRedirectUri: window.location.href,
    });
    if (ready && pending?.linkToken) {
      console.log('[PlaidOAuthReturn] opening Plaid Link (OAuth continuation)');
      open();
    }
  }, [ready, pending?.linkToken, open]);

  return (
    <Center h="100vh">
      <VStack spacing={4}>
        <Spinner size="xl" color="brand.secondary" thickness="4px" />
        <Text>Completing bank connection…</Text>
      </VStack>
    </Center>
  );
};
